import { extractMoneyCandidates, MONEY_PARSE_REASON } from "../money-parser.js";
import { scrubPiiForLlm } from "../pii-scrubber.js";
import type { AiCapabilityPage, AiCollectionCadence } from "../registry/schema.js";
import { hasNegationOrDoubt } from "../resolvers/enum-slot.js";
import { HEAD_COUNT_NOUNS, PRONOUN_LIKE_PERSON_NOUNS } from "../vi-count-nouns.js";
import { isUnaccentedText } from "../resolvers/name-text.js";
import { sameWord, wordsOf, type Word } from "../resolvers/vi-words.js";
import type { AiInterpretDecision, AiInterpretRejectReason } from "./confidence-policy.js";
import type { InterpreterSpanLike } from "./output.js";

/**
 * 🔴🔴 AI-COL-OPEN-PHRASING (@UC0195 @UC0220) — LƯỚI MỞ ĐỢT THU theo khuôn ĐÓNG "nghĩa vụ đóng THEO ĐẦU NGƯỜI".
 *
 * ## Vấn đề (đo, không đoán — LLM thật 10/10 + 11/10, gpt-5.4-nano, persona thật)
 *  - *"học kỳ 2 mỗi nhà đóng thêm 300k nhé"* ⇒ Người phiên dịch `unknown` (độ chắc 0,18–0,2) 5/5 ⇒ menu "chưa rõ ý";
 *  - *"moi be dong 150k tien di da ngoai"* ⇒ RECORD-PAID 5/5 với `nguoi: "moi be"` ⇒ "Mình chưa rõ bạn nói ai. Ai đã đóng?" — sai VIỆC;
 *  - *"thang nao cung thu 50k moi nguoi nhe"* ⇒ `unknown` 3/5;
 *  - M6: *"thu mỗi bé 200k tiền liên hoan cuối năm"* ⇒ COL-EQUAL nhưng không gạch `tenDot` ⇒ phiếu "Thu quỹ tháng bạn bấm Đồng ý" — mất mục
 *    đích người dùng vừa nói.
 * Tầng tìm trang không lỗi (đo offline: ba trang mở đợt thu đều trong top-8 của cả bốn câu). Vá Sổ/prompt là đổi bài thi của mọi ca có
 * COL-* trong top-8 — lưới tất định rẻ hơn, đo được, và không đổi một byte prompt (0 ca eval phải thu lại).
 *
 * ## Khuôn (MỌI chữ của câu phải thuộc khuôn — luật chung 1 của `AI_DETERMINISTIC_NETS`)
 *
 *   {KỲ | LẶP}*  ( MỞ-ĐỢT-THU [LẶP] [quỹ …] [tiền <mục đích>] ( SỐ [MỖI-NGƯỜI] | MỖI-NGƯỜI [ĐÓNG] SỐ )
 *               | thu [LẶP] [quỹ …] [tiền <mục đích>] ( SỐ MỖI-NGƯỜI | MỖI-NGƯỜI SỐ )
 *               | MỖI-NGƯỜI ĐÓNG [thêm|đều] [quỹ …] SỐ )
 *   [đều] { LẶP | KỲ | tiền <mục đích> | HẠN <chữ> }*  {trợ từ cuối}*
 *
 *  - MỖI-NGƯỜI = "mỗi <đơn vị người>" | "mọi người" (đầu câu) · "mỗi|một|1 <đơn vị người>" (SAU số). "một người đóng 100k" ở đầu câu là MỘT người
 *    đã đóng, không phải nghĩa vụ ⇒ ngoài khuôn. Đơn vị người là tập đóng (người · bé · nhà · bạn · phụ huynh · suất · …); "mỗi lần/tháng" không phải.
 *  - "thu <số>" mà không "mỗi <người>" ⇒ ngoài khuôn (tổng hay mỗi người?); "mở đợt thu <số>" thì số là ô "mỗi người" của chính FORM mở đợt.
 *  - SỐ = một cụm tiền mà bộ đọc tiền DUY NHẤT (`extractMoneyCandidates`) đọc ra MỘT số, hoặc báo MƠ HỒ VỀ BẬC ("2 trăm") — cụm mơ hồ vẫn đi
 *    tiếp NGUYÊN CHỮ tới ô tiền để bộ đọc HỎI (@UC0162, §4.2). Không có số thứ hai nào trong câu (mục đích chứa số tiền ⇒ ngoài khuôn).
 *  - Kiểu đợt: có LẶP ("hàng tháng", "tháng nào cũng", "mỗi quý", …) ⇒ `recurring`; có HẠN ("hạn …", "deadline …") ⇒ `deadline`; cả hai ⇒ ngoài khuôn
 *    (không đoán); không có ⇒ `once`.
 *  - Việc ĐÃ xảy ra ("đã", "rồi", "xong") · phủ định / nghi vấn / nửa chắc (`hasNegationOrDoubt`, luật chung 4) ⇒ ngoài khuôn: *"bé Đức Anh đóng
 *    rồi"*, *"mỗi bé đóng 150k rồi"* là GHI NHẬN, không phải mở đợt.
 *  - KỲ ("tháng 9", "học kỳ 2", "tháng này") — kỳ TRẦN (không chữ tương lai "này/sau/tới/mới/nay/sắp tới") có thể là KỂ kỳ đã qua ("tháng 9 mỗi bé
 *    đóng 150k" = hồi tháng 9 mỗi bé đã đóng) ⇒ chỉ thuộc khuôn khi câu kết bằng trợ từ ĐỀ NGHỊ ("nhé/nha/nhe/nhá/nhen/đi"). "thêm" một mình không đủ
 *    (review #1372 MEDIUM-1).
 *  - Mục đích ("tiền quà 8/3") chỉ thành TÊN ĐỢT khi có ít nhất một chữ KHÔNG chung (luật (c): "tiền quỹ lớp" không phải tên), không chứa placeholder
 *    của bộ cạo PII ("[SĐT]") và không mang chữ tiền ra/hoàn/phạt/ứng/nợ (review #1372 LOW-1/LOW-2) — khuôn vẫn khớp, tên đợt về mặc định.
 *
 * ## Hai lưới, mỗi lưới MỘT việc (luật chung 2)
 *  - `collectionOpenPageFromSentence` CHỌN TRANG (Sổ khai `collectionFromSentence.cadence`), chỉ khi LLM THẬT đã đọc câu (luật chung 3) và hoặc bỏ cuộc
 *    (`not_understood`/`low_confidence`) ⇒ MỞ, hoặc chọn một trang KHÔNG mở đợt thu ⇒ chỉ MỞ khi Phiếu gạch ô người bằng chính LƯỢNG TỪ ("moi be" —
 *    phân phối, không phải một người: luật (c)); không có bằng chứng đó ⇒ HỎI A/B [trang LLM, trang mở đợt thu] (review #1372 MEDIUM-2). LLM đã chọn
 *    một trang mở đợt thu (hay kẹt A/B giữa hai trang đó) ⇒ KHÔNG đè.
 *  - `fillCollectionSlotsFromSentence` ĐIỀN Ô trên trang đã mở (Sổ khai `amount`/`name`): chữ SỐ của câu ⇒ ô tiền mỗi người, chữ MỤC ĐÍCH ⇒ ô tên
 *    đợt — chỉ ô Phiếu chưa gạch, và chỉ khi kiểu câu = kiểu trang. Không bao giờ đè span model; Phiếu (`ai_audit_log`) không bị sửa.
 * Phiếu xác nhận T2 (in số người × số tiền, tên đợt, hạn) + nút Đồng ý vẫn là chốt cuối (AI-INV-4). Thuần, 0 LLM, 0 I/O.
 */
export interface PerHeadCollection {
  readonly cadence: AiCollectionCadence;
  /** Chữ SỐ TIỀN của câu (NFC, đã cạo PII) — đi NGUYÊN CHỮ tới resolver ô tiền. */
  readonly amount: string;
  /** Chữ MỤC ĐÍCH ("tiền quà 8/3") — `null` khi câu không nói, hoặc chỉ nói chữ chung. */
  readonly purpose: string | null;
}

type Phrase = readonly Word[];
const phrases = (...xs: string[]): readonly Phrase[] => xs.map((x) => wordsOf(x));

/**
 * Đơn vị NGƯỜI sau "mỗi/một" — tập đóng. AI-INTERP N3: tập người từ từ vựng chung (cả "em"/"con"/"hộ"/"nhà": sau "mỗi" chỉ còn nghĩa người); phần
 * RIÊNG của lưới: "anh em" · viết tắt "hs"/"ph"/"ae" · "member"/"mem". Dạng không dấu: `wordsOf` + `sameWord` tự nới cho chữ gõ không dấu.
 */
const PERSON_UNITS = phrases(...[...HEAD_COUNT_NOUNS, ...PRONOUN_LIKE_PERSON_NOUNS].map((n) => n.vi), "anh em", "hs", "ph", "ae", "member", "mem");
const EACH = phrases("mỗi");
const EACH_AFTER = phrases("mỗi", "một", "1");
const EVERYONE = phrases("mọi người", "mỗi đầu người");
const COLLECT = phrases("thu");
const OPEN_VERBS = phrases("mở", "tạo", "lập");
const OPEN_NOUNS = phrases("đợt", "lần");
const NEW = phrases("mới");
const PAY_VERBS = phrases("đóng góp", "đóng", "nộp", "góp");
const PAY_MODS = phrases("thêm", "đều");
const EVENLY = phrases("đều");
const FUND = phrases("quỹ");
const FUND_KINDS = phrases("lớp học", "lớp", "đội", "nhóm", "chung", "câu lạc bộ", "clb", "hội");
const MONEY_NOUN = phrases("tiền");
const RECURRING = phrases(
  "định kỳ hàng tháng", "định kỳ hằng tháng", "định kỳ mỗi tháng", "định kỳ hàng quý", "định kỳ hằng quý", "định kỳ mỗi quý",
  "tháng nào cũng", "quý nào cũng", "hàng tháng", "hằng tháng", "mỗi tháng", "theo tháng", "hàng quý", "hằng quý", "mỗi quý", "theo quý", "định kỳ",
);
const PERIOD_WORDS = phrases("này", "sau", "tới", "mới", "nay");
/** Chữ TƯƠNG LAI ngay sau một kỳ ĐÁNH SỐ ("học kỳ 2 này", "tháng 10 tới", "học kỳ 2 sắp tới"). */
const FUTURE_AFTER_NUMBER = phrases("sắp tới", "này", "sau", "tới", "mới", "nay");
const TIME_FIXED = phrases(
  "đầu năm học", "cuối năm học", "đầu học kỳ", "cuối học kỳ", "năm học này", "năm học mới", "đầu năm", "cuối năm", "đầu kỳ", "cuối kỳ", "đầu tháng", "cuối tháng", "sắp tới",
);
/** Phần của `TIME_FIXED` MANG chữ tương lai — phần còn lại ("đầu năm học", "cuối tháng") là kỳ TRẦN. */
const TIME_FIXED_FUTURE = phrases("năm học này", "năm học mới", "sắp tới");
/**
 * Trợ từ ĐỀ NGHỊ cuối câu — lời đề nghị hướng tới (review #1372 MEDIUM-1): câu có kỳ TRẦN chỉ thuộc khuôn khi kết bằng một trong các chữ này. "ạ"
 * (lễ phép) · "luôn"/"với" (nhấn) KHÔNG phải đề nghị — "tháng 9 mỗi bé đóng 150k ạ" vẫn là lời kể được.
 */
const REQUEST_TAIL = phrases("nhé", "nha", "nhe", "nhá", "nhen", "đi");
const TIME_HEADS = phrases("học kỳ", "tháng", "tuần", "năm", "đợt", "kỳ", "lần");
const ORDINALS = phrases("một", "hai", "ba", "i", "ii");
const DEADLINE_HEADS = phrases("hạn chót", "hạn", "deadline", "chậm nhất");
const TAIL = phrases("nhé", "nha", "nhe", "nhá", "nhen", "đi", "ạ", "em", "chị", "anh", "ơi", "luôn", "giúp", "giùm", "với", "mọi người", "cả nhà", "các mẹ");
/** Đuôi ĐƠN VỊ của một cụm số ("2 trăm nghìn", "300 k"). */
const AMOUNT_UNITS = phrases("nghìn", "ngàn", "triệu", "củ", "trăm", "chục", "k", "tr", "đồng");
/** Chữ CHUNG — một mục đích chỉ gồm những chữ này không phải tên đợt (luật (c)). */
const GENERIC_PURPOSE = phrases("quỹ", "lớp", "đội", "nhóm", "chung", "này", "đợt", "kỳ", "thu", "đóng", "học", "tháng", "năm");
/**
 * Việc ĐÃ xảy ra — trong phần mục đích cũng không được (câu ghi nhận trá hình). "đã"/"vừa" chỉ tính khi GÕ CÓ DẤU: không dấu "da"/"vua" còn là
 * "dã" (dã ngoại) / "vừa" (vừa đủ) — "tien di da ngoai" là mục đích thật (đo: câu của hanh). "rồi"/"xong" không dấu vẫn là việc đã xong.
 */
const PAST = phrases("rồi", "xong");
const PAST_ACCENTED_ONLY = phrases("đã", "vừa");
/**
 * Review #1372 LOW-2 — chữ của TIỀN RA / HOÀN / PHẠT / NỢ trong mục đích ⇒ mục đích KHÔNG thành tên đợt (tên đợt về mặc định; người dùng vẫn thấy câu
 * của mình trên thẻ). Cụm nhiều chữ khớp cả không dấu; chữ ĐƠN chỉ tính khi GÕ CÓ DẤU (như `PAST_ACCENTED_ONLY`): không dấu "hoan" còn là liên HOAN,
 * "phat" là PHÁT quà, "ung" là ỦNG hộ, "no" là no/nó.
 */
const NOT_A_NAME = phrases("hoàn lại", "hoàn tiền", "trả lại", "trả nợ", "nộp phạt", "tạm ứng", "ứng trước");
const NOT_A_NAME_ACCENTED_ONLY = phrases("hoàn", "phạt", "ứng", "nợ");

class Cursor {
  constructor(
    readonly text: string,
    readonly w: readonly Word[],
  ) {}

  /** Độ dài (số từ) của cụm DÀI NHẤT trong `list` khớp tại `i`, 0 nếu không cụm nào. */
  match(i: number, list: readonly Phrase[]): number {
    let best = 0;
    for (const p of list) {
      if (p.length > best && i + p.length <= this.w.length && p.every((pw, k) => sameWord(this.w[i + k]!, pw))) best = p.length;
    }
    return best;
  }

  /** Từ `i` tới hết câu chỉ toàn trợ từ cuối. */
  allTail(i: number): boolean {
    let j = i;
    while (j < this.w.length) {
      const n = this.match(j, TAIL);
      if (n === 0) return false;
      j += n;
    }
    return true;
  }

  slice(a: number, b: number): string {
    return this.text.slice(this.w[a]!.start, this.w[b - 1]!.end);
  }

  /**
   * Cụm KỲ ("học kỳ 2", "tháng 10 tới", "tháng này", "cuối năm") tại `i` ⇒ số từ + `future` (cụm MANG chữ tương lai), hoặc `null`. Kỳ TRẦN ("tháng 9",
   * "hk2", "đầu năm học") có thể là kể kỳ đã qua — người gọi quyết (review #1372 MEDIUM-1).
   */
  time(i: number): { readonly n: number; readonly future: boolean } | null {
    const fixed = this.match(i, TIME_FIXED);
    if (fixed > 0) return { n: fixed, future: this.match(i, TIME_FIXED_FUTURE) === fixed };
    let n = /^hk\d$/u.test(this.w[i]?.key ?? "") ? 1 : 0;
    if (n === 0) {
      const head = this.match(i, TIME_HEADS);
      if (head === 0) return null;
      const next = this.w[i + head];
      if (next === undefined) return null;
      const period = this.match(i + head, PERIOD_WORDS);
      if (period > 0) return { n: head + period, future: true };
      if (!/^\d{1,2}$/u.test(next.key) && this.match(i + head, ORDINALS) === 0) return null;
      n = head + 1;
    }
    const after = this.match(i + n, FUTURE_AFTER_NUMBER);
    return { n: n + after, future: after > 0 };
  }

  /** Có chữ nào thuộc `list` trong `[from, hết câu)`. */
  hasAny(from: number, list: readonly Phrase[]): boolean {
    for (let j = from; j < this.w.length; j++) if (this.match(j, list) > 0) return true;
    return false;
  }

  /** Cụm SỐ TIỀN tại `i` (chữ số đầu + nối "." / "," liền + tối đa 2 từ đơn vị) — chỉ khi bộ đọc tiền nhận là tiền (một số, hoặc mơ hồ về bậc). */
  amount(i: number): number {
    const first = this.w[i];
    if (first === undefined || !/^\d/u.test(first.key)) return 0;
    let j = i + 1;
    while (j < this.w.length && /^\d/u.test(this.w[j]!.key) && this.w[j]!.start === this.w[j - 1]!.end + 1 && /[.,]/u.test(this.text[this.w[j - 1]!.end] ?? "")) j += 1;
    for (let u = 0; u < 2 && j < this.w.length && this.match(j, AMOUNT_UNITS) === 1; u++) j += 1;
    const r = extractMoneyCandidates(this.slice(i, j));
    const moneyish = r.kind === "resolved" || (r.kind === "ambiguous" && r.reason === MONEY_PARSE_REASON.AMBIGUOUS_MAGNITUDE_WORD);
    return moneyish ? j - i : 0;
  }

  /** "mỗi <đơn vị người>" / "mọi người" ở vị trí CHỦ NGỮ. */
  perHeadSubject(i: number): number {
    const all = this.match(i, EVERYONE);
    if (all > 0) return all;
    const each = this.match(i, EACH);
    const unit = each > 0 ? this.match(i + each, PERSON_UNITS) : 0;
    return unit > 0 ? each + unit : 0;
  }

  /** "mỗi|một|1 <đơn vị người>" / "mọi người" SAU số tiền. */
  perHeadAfter(i: number): number {
    const all = this.match(i, EVERYONE);
    if (all > 0) return all;
    const each = this.match(i, EACH_AFTER);
    const unit = each > 0 ? this.match(i + each, PERSON_UNITS) : 0;
    return unit > 0 ? each + unit : 0;
  }

  /** "quỹ [lớp|đội|…]" — đối tượng CHUNG (không thành tên đợt). */
  fundObject(i: number): number {
    const f = this.match(i, FUND);
    return f > 0 ? f + this.match(i + f, FUND_KINDS) : 0;
  }

  /** "mở|tạo|lập [đợt|lần] thu [mới]". */
  openPhrase(i: number): number {
    const v = this.match(i, OPEN_VERBS);
    if (v === 0) return 0;
    const noun = this.match(i + v, OPEN_NOUNS);
    const c = this.match(i + v + noun, COLLECT);
    if (c === 0) return 0;
    const n = v + noun + c;
    return n + this.match(i + n, NEW);
  }

  /** Điểm dừng của một đoạn chữ tự do (mục đích / hạn): hết câu · còn lại toàn trợ từ · bắt đầu LẶP/HẠN · (tuỳ chọn) bắt đầu MỖI-NGƯỜI/SỐ. */
  freeEnd(from: number, stopAtBody: boolean): number {
    for (let j = from; j < this.w.length; j++) {
      if (this.allTail(j) || this.match(j, RECURRING) > 0 || this.match(j, DEADLINE_HEADS) > 0) return j;
      if (stopAtBody && (this.perHeadSubject(j) > 0 || this.amount(j) > 0)) return j;
    }
    return this.w.length;
  }

  /** "tiền <chữ…>" tại `i` ⇒ [số từ, mục đích|null], hoặc `null` khi không phải mục đích hợp lệ (rỗng · việc đã xảy ra · chứa số tiền). */
  purpose(i: number, stopAtBody: boolean): { readonly n: number; readonly text: string | null } | null {
    const t = this.match(i, MONEY_NOUN);
    if (t === 0) return null;
    const end = this.freeEnd(i + t, stopAtBody);
    if (end <= i + t) return null;
    for (let j = i + t; j < end; j++) if (this.match(j, PAST) > 0 || (!this.w[j]!.plain && this.match(j, PAST_ACCENTED_ONLY) > 0)) return null;
    const text = this.slice(i, end);
    if (extractMoneyCandidates(text).kind !== "none") return null;
    let generic = true;
    let notAName = /[[\]]/u.test(text); // LOW-1: placeholder của bộ cạo PII ("tiền [SĐT") không phải tên đợt
    for (let j = i + t; j < end; j++) {
      generic &&= this.match(j, GENERIC_PURPOSE) === 1;
      notAName ||= this.match(j, NOT_A_NAME) > 0 || (!this.w[j]!.plain && this.match(j, NOT_A_NAME_ACCENTED_ONLY) > 0);
    }
    return { n: end - i, text: generic || notAName ? null : text };
  }
}

/**
 * Câu có chữ LẶP ("hàng tháng", "mỗi tháng", "tháng nào cũng", "định kỳ", "hằng quý"…) — CÙNG tập `RECURRING` của khuôn ở trên (một nguồn). UX-COLLECTION-PERIOD
 * dùng để biết câu nói một THÁNG ("thu quỹ tháng 11 …") mà KHÔNG nói lặp thì không phải lịch thu định kỳ.
 */
export function hasRecurringMarker(sentence: string): boolean {
  const text = sentence.normalize("NFC");
  return new Cursor(text, wordsOf(text)).hasAny(0, RECURRING);
}

/**
 * Đầu cụm KỲ (khuôn `Cursor.time`) KHÔNG phải kỳ của đợt mà lưới kỳ phải hỏi (W-1): "tháng"/"đầu|cuối tháng" là việc của bộ đọc THÁNG
 * (`readCollectionPeriod`; "cuối tháng" = đúng hạn mặc định của COL-EQUAL) · "tuần" là một hạn, không phải kỳ · "đợt"/"lần" đếm đợt · "sắp tới" không nói kỳ nào.
 */
/** "năm học" trơn (W-1, review #1447 MEDIUM-2). */
const SCHOOL_YEAR = phrases("năm học");
const NOT_A_COLLECTION_TERM = phrases("tháng", "đầu tháng", "cuối tháng", "tuần", "đợt", "lần", "sắp tới");

/**
 * 🔴 W-1 UX-COL-OPEN-PERIOD (@UC0195 @UC0196) — các cụm KỲ của câu theo CHÍNH khuôn KỲ của lưới mở đợt thu (`Cursor.time` — `TIME_FIXED` "năm học mới",
 * "đầu năm" · "học kỳ 2" · "hk2" · "năm sau/tới/mới/nay"; một bảng, không khuôn thứ hai), TRỪ cụm mở bằng `NOT_A_COLLECTION_TERM`. Vị trí `[start, end)`
 * trên chuỗi NFC đã truyền. Lưới kỳ (`readCollectionPeriod`) dùng để biết câu NÓI một kỳ mà bộ đọc tháng không đọc ⇒ HỎI. Thuần.
 */
export function collectionTermSpans(text: string): { readonly start: number; readonly end: number }[] {
  const c = new Cursor(text, wordsOf(text));
  const unaccented = isUnaccentedText(text);
  const out: { readonly start: number; readonly end: number }[] = [];
  for (let i = 0; i < c.w.length; ) {
    // Review điều phối #1447 MEDIUM-2: "năm học" + số năm ("năm học 2026-2027", "năm học 2026") — khuôn KỲ chỉ nhận "năm học này/mới", nhưng "năm học"
    // ở đâu cũng là một lời về KỲ (năm học cụ thể vẫn không có NGÀY hạn) ⇒ tính, để lưới kỳ HỎI hạn. Cùng luật dấu chữ "năm".
    const t = c.time(i) ?? (c.match(i, SCHOOL_YEAR) > 0 ? { n: SCHOOL_YEAR[0]!.length } : null);
    if (t === null) {
      i += 1;
      continue;
    }
    let yearOk = true;
    for (let k = i; k < i + t.n; k++) yearOk &&= isYearWord(text, c.w[k]!, k, unaccented);
    if (yearOk && c.match(i, NOT_A_COLLECTION_TERM) === 0) out.push({ start: c.w[i]!.start, end: c.w[i + t.n - 1]!.end });
    i += t.n;
  }
  return out;
}

const YEAR_WORD = wordsOf("năm")[0]!;
/**
 * Review W-1 HIGH-1 — "nam" bỏ dấu ≡ "năm" ≡ tên "Nam" ("bé Nam mới vào lớp", "của Nam tới"): `sameWord` nới cho chữ không dấu ⇒ khuôn KỲ đọc oan. Cùng luật
 * dấu của bảng ngày (`periodHeadOk`) và lời năm sau (`nextYearMarkers`): câu CÓ dấu ⇒ đúng chữ có dấu "năm"; câu không dấu ⇒ chữ thường (chữ HOA giữa câu là
 * tên; chữ hoa đầu câu do bàn phím). Từ không phải "nam" ⇒ không xét.
 */
function isYearWord(text: string, w: Word, at: number, unaccentedSentence: boolean): boolean {
  if (w.key !== YEAR_WORD.key) return true;
  if (!unaccentedSentence) return !w.plain && sameWord(w, YEAR_WORD);
  const raw = text.slice(w.start, w.end);
  const sentenceStart = at === 0 || /[.!?…]\s*$/u.test(text.slice(0, w.start));
  return raw === w.key || (sentenceStart && raw.toLocaleLowerCase("vi") === w.key);
}

/** Câu (một lượt chữ) là lời MỞ ĐỢT THU theo đầu người, theo khuôn ở docblock đầu file — hoặc `null` (ngoài khuôn). */
export function parsePerHeadCollection(sentence: string): PerHeadCollection | null {
  const text = scrubPiiForLlm(sentence).text.normalize("NFC").trim();
  if (text === "" || hasNegationOrDoubt(text)) return null;
  const c = new Cursor(text, wordsOf(text));
  let i = 0;
  let recurring = false;
  let deadline = false;
  let purpose: string | null = null;
  let hasPurpose = false;
  let bareTime = false; // có một kỳ TRẦN (không chữ tương lai) — MEDIUM-1

  // {KỲ | LẶP}* ở đầu câu
  for (;;) {
    const r = c.match(i, RECURRING);
    if (r > 0) { recurring = true; i += r; continue; }
    const t = c.time(i);
    if (t !== null) { bareTime ||= !t.future; i += t.n; continue; }
    break;
  }

  const open = c.openPhrase(i);
  const collect = open === 0 ? c.match(i, COLLECT) : 0;
  let amountAt = -1;
  let amountLen = 0;
  if (open > 0 || collect > 0) {
    i += open + collect;
    const r = c.match(i, RECURRING);
    if (r > 0) { recurring = true; i += r; }
    i += c.fundObject(i);
    const p = c.purpose(i, true);
    if (p !== null) { purpose = p.text; hasPurpose = true; i += p.n; }
    const a = c.amount(i);
    if (a > 0) {
      amountAt = i; amountLen = a; i += a;
      const ph = c.perHeadAfter(i);
      if (ph === 0 && open === 0) return null; // "thu <số>" không "mỗi <người>" ⇒ tổng hay mỗi người?
      i += ph;
    } else {
      const ph = c.perHeadSubject(i);
      if (ph === 0) return null;
      i += ph;
      const v = c.match(i, PAY_VERBS);
      if (v > 0) { i += v; i += c.match(i, PAY_MODS); }
      const a2 = c.amount(i);
      if (a2 === 0) return null;
      amountAt = i; amountLen = a2; i += a2;
    }
  } else {
    const ph = c.perHeadSubject(i);
    if (ph === 0) return null;
    i += ph;
    const v = c.match(i, PAY_VERBS);
    if (v === 0) return null;
    i += v;
    i += c.match(i, PAY_MODS);
    i += c.fundObject(i);
    const a = c.amount(i);
    if (a === 0) return null;
    amountAt = i; amountLen = a; i += a;
  }

  // [đều] { LẶP | KỲ | tiền <mục đích> | HẠN <chữ> }*  {trợ từ cuối}*
  i += c.match(i, EVENLY);
  while (!c.allTail(i)) {
    const r = c.match(i, RECURRING);
    if (r > 0) { recurring = true; i += r; continue; }
    const d = c.match(i, DEADLINE_HEADS);
    if (d > 0) {
      const end = c.freeEnd(i + d, false);
      if (end <= i + d) return null;
      deadline = true; i = end; continue;
    }
    if (!hasPurpose) {
      const p = c.purpose(i, false);
      if (p !== null) { purpose = p.text; hasPurpose = true; i += p.n; continue; }
    }
    const t = c.time(i);
    if (t !== null) { bareTime ||= !t.future; i += t.n; continue; }
    return null;
  }
  if (recurring && deadline) return null;
  // MEDIUM-1: kỳ TRẦN ("tháng 9 mỗi bé đóng 150k") có thể là KỂ kỳ đã qua ⇒ chỉ thuộc khuôn khi câu kết bằng trợ từ ĐỀ NGHỊ (i = đầu phần trợ từ cuối).
  if (bareTime && !c.hasAny(i, REQUEST_TAIL)) return null;
  return { cadence: recurring ? "recurring" : deadline ? "deadline" : "once", amount: c.slice(amountAt, amountAt + amountLen), purpose };
}

type CollectionPageLike = Pick<AiCapabilityPage, "id" | "collectionFromSentence" | "slots">;

export interface CollectionOpenNetInput<P extends CollectionPageLike> {
  readonly sentence: string;
  /** Trang ĐƯỢC PHÉP (cổng vai/cờ — @UC0152). Lưới chỉ chọn trong đây. */
  readonly allowed: readonly P[];
  /** Quyết định của chính sách Người phiên dịch (`decideInterpretation`) cho Phiếu THẬT của lượt này. */
  readonly decision: AiInterpretDecision;
  readonly rejected: AiInterpretRejectReason | undefined;
  readonly simulated: boolean;
  /** Trang của Phiếu LLM (`envelope.page`) — chỗ tra loại ô của `llmSpans`. */
  readonly llmPage: string | undefined;
  /** Span ĐÃ NEO của Phiếu LLM (`envelope.spans`). */
  readonly llmSpans: readonly InterpreterSpanLike[];
}

/** Kết quả lưới chọn trang: MỞ trang mở đợt thu, hoặc HỎI A/B (trang LLM chọn · trang mở đợt thu) — cùng hình `AiInterpretDecision` để Người làm dùng lại nhánh A/B sẵn có. */
export type CollectionOpenNetOutcome<P> =
  | { readonly action: "run"; readonly page: P }
  | { readonly action: "ask_ab"; readonly pages: readonly [string, string] };

/**
 * Review #1372 MEDIUM-2 — Phiếu LLM gạch ô NGƯỜI (`type: "member"` của trang LLM chọn) bằng ĐÚNG một LƯỢNG TỪ của khuôn ("mỗi bé", "moi be", "mọi
 * người" — `perHeadSubject`, một nguồn với bộ đọc câu): LLM đã coi lượng từ phân phối là MỘT người ⇒ bằng chứng nó đọc sai câu (luật (c)).
 */
function llmTookQuantifierForPerson<P extends CollectionPageLike>(input: CollectionOpenNetInput<P>): boolean {
  const page = input.allowed.find((p) => p.id === input.llmPage);
  if (page === undefined) return false;
  const personSlots = new Set(page.slots.filter((s) => s.type === "member").map((s) => s.id));
  return input.llmSpans.some((sp) => {
    if (!personSlots.has(sp.slot)) return false;
    const t = sp.text.normalize("NFC").trim();
    const c = new Cursor(t, wordsOf(t));
    return c.w.length > 0 && c.perHeadSubject(0) === c.w.length;
  });
}

/**
 * LƯỚI CHỌN TRANG (kind `page`). Trả MỞ trang mở đợt thu / HỎI A/B, hoặc `null` (đường cũ y nguyên). Luật ở docblock đầu file; ở đây năm vế:
 *  1. LLM THẬT đã đọc câu (`rejected` vắng, không `simulated`) — luật chung 3: không vòng qua kill-switch / LLM giả không mở việc nào (§4.2);
 *  2. LLM đã chọn trang mở đợt thu (hay kẹt A/B giữa hai trang mở đợt thu) ⇒ giữ lựa chọn của LLM; menu vì lý do khác bỏ cuộc ⇒ giữ;
 *  3. CẢ câu thuộc khuôn (`parsePerHeadCollection`);
 *  4. trang khai đúng kiểu đợt của câu nằm trong `allowed`;
 *  5. (MEDIUM-2) LLM bỏ cuộc ⇒ MỞ. LLM chọn một trang KHÁC ⇒ chỉ MỞ khi nó gạch lượng từ vào ô người (`llmTookQuantifierForPerson`); không có bằng
 *     chứng đó: `run` ⇒ HỎI A/B [trang LLM, trang mở đợt thu]; LLM tự kẹt A/B ⇒ giữ câu hỏi A/B của nó (không A/B thứ hai).
 */
export function collectionOpenPageFromSentence<P extends CollectionPageLike>(input: CollectionOpenNetInput<P>): CollectionOpenNetOutcome<P> | null {
  if (input.rejected !== undefined || input.simulated) return null;
  const { decision, allowed } = input;
  const opensCollection = (id: string): boolean => allowed.some((p) => p.id === id && p.collectionFromSentence !== undefined);
  if (decision.action === "menu" && decision.reason !== "not_understood" && decision.reason !== "low_confidence") return null;
  if (decision.action === "run" && opensCollection(decision.page)) return null;
  if (decision.action === "ask_ab" && decision.pages.every(opensCollection)) return null;
  const parsed = parsePerHeadCollection(input.sentence);
  if (parsed === null) return null;
  const page = allowed.find((p) => p.collectionFromSentence?.cadence === parsed.cadence);
  if (page === undefined) return null;
  if (decision.action === "menu" || llmTookQuantifierForPerson(input)) return { action: "run", page };
  return decision.action === "run" ? { action: "ask_ab", pages: [decision.page, page.id] } : null;
}

export interface CollectionSlotFill {
  readonly slot: string;
  /** Chữ CỦA CÂU (NFC, đã cạo PII) đưa cho resolver của ô. */
  readonly text: string;
}

/**
 * LƯỚI ĐIỀN Ô (kind `slot`) — trang đã mở (LLM chọn, hoặc lưới chọn trang ở trên). Chỉ ô Sổ khai (`collectionFromSentence.amount`/`name`), chỉ
 * ô Phiếu ĐÃ NEO chưa gạch, chỉ khi kiểu câu = kiểu trang (câu "hàng tháng" trên trang đợt một lần ⇒ không điền: số đó là số MỖI KỲ).
 */
export function fillCollectionSlotsFromSentence(
  sentence: string,
  anchoredSpans: readonly InterpreterSpanLike[],
  page: Pick<AiCapabilityPage, "collectionFromSentence"> | null,
): CollectionSlotFill[] {
  const decl = page?.collectionFromSentence;
  if (decl === undefined) return [];
  const parsed = parsePerHeadCollection(sentence);
  if (parsed === null || parsed.cadence !== decl.cadence) return [];
  const marked = (slot: string): boolean => anchoredSpans.some((s) => s.slot === slot);
  const fills: CollectionSlotFill[] = [];
  if (!marked(decl.amount)) fills.push({ slot: decl.amount, text: parsed.amount });
  if (decl.name !== undefined && parsed.purpose !== null && !marked(decl.name)) fills.push({ slot: decl.name, text: parsed.purpose });
  return fills;
}
